import clsx from 'clsx';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm, useFieldArray, Controller, SubmitHandler, UseFormTrigger } from 'react-hook-form';

import Icon, { ICONS, ICON_TYPES } from '@/src/components/common/Elements/Icon/Icon';
import Label from '@/src/components/common/Elements/Label/Label';
import { MODAL_TYPE, ACTION_POSITIONS } from '@/src/components/common/Elements/Modal/Config';
import useConfirmPopup from '@/src/components/common/Elements/Modal/useConfirmPopup';
import { showToast } from '@/src/globalFunction';
import { getDecimalCount, parseNumber } from '@/src/util/numbers';

import LineItemsTable, { getIsTaxRequired } from './LineItemsTable';

import ActionButton from '../../../../Elements/ActionButton/ActionButton';
import ComputationDisplayLayout from '../../../../Elements/ComputationDisplayLayout/ComputationDisplayLayout';
import {
  TaxCalculationEnum,
  TreatmentCalculator,
} from '../../../../models/Treatment/TreatmentCalculator';
import { DropdownOption } from '../../../../types';
import { CommonAutomatedLineEntry } from '../../../../types/draft';
import { LineEntryForm, TreatmentForm } from '../../../../types/treatment';
import useTreatmentCalculator from '../../../TreatmentForm/useTreatmentCalculator';
import Computations from '../../Computations/Computations';
import SelectField from '../../Fields/SelectField/SelectField';

interface LineItemsForm {
  lineItems: LineEntryForm[] | (LineEntryForm & CommonAutomatedLineEntry)[];
  taxCalculation: TreatmentForm['taxCalculation'];
}

export type TableEvents = {
  onUnitPriceBlur: (rowIndex: number, unitPrice: string) => void;
  onQuantityBlur: (rowIndex: number, quantity: string) => void;
  onRemove: (rowIndex: number) => void;
  trigger: UseFormTrigger<LineItemsForm>;
};

const defaultLineEntryData: LineEntryForm = {
  item: null,
  account: null,
  description: '',
  qty: null,
  unitPrice: null,
  totalAmount: null,
  taxCode: null,
  class: null,
};

const formatUnitPriceAndQuantity = (value: string) => {
  const parsedNumber = parseNumber(value);
  const isWholeNumber = parsedNumber % 1 === 0;
  if (isWholeNumber) return parsedNumber.toFixed(2);
  return getDecimalCount(parsedNumber) > 4 ? parsedNumber.toFixed(4) : parsedNumber.toString();
};

const LineItemsForm = ({
  dropdowns,
  lineItems,
  taxCalculation,
  currency,
  onClose,
  setLineItems,
  setTaxCalculation,
  onConfirmClearLines,
  isAutomated,
  taxCalculationIsAutomated,
  draftLineEntries,
  draftTaxCalculation,
  isPrefilled = false,
}) => {
  const {
    register,
    control,
    watch,
    handleSubmit: formHandleSubmit,
    formState,
    getValues,
    setValue,
    reset,
    trigger,
  } = useForm<LineItemsForm>({
    defaultValues: {
      lineItems: lineItems.length > 0 ? [...lineItems] : [defaultLineEntryData],
      taxCalculation: taxCalculation,
    },
    mode: 'onBlur',
  });

  const { isDirty, isValid } = formState;

  const [DiscardChangesModal, confirmDiscard] = useConfirmPopup({
    classNames: '',
    modalWidth: '600px',
    modalType: MODAL_TYPE.danger,
    actionPosition: ACTION_POSITIONS.end,
    title: 'Unsaved changes',
    icon: { icon: ICONS.info, iconType: ICON_TYPES.outlined },
    message: `Changes you made will not be saved. Are you sure you want to leave this page?`,
    confirmBtn: { label: 'Leave without saving' },
    cancelBtn: { label: 'Stay on this Page' },
  });

  const { fields, append, remove, update } = useFieldArray<LineItemsForm, 'lineItems', 'id'>({
    control,
    name: 'lineItems',
  });

  const watchedFields = watch();

  const { totals } = useTreatmentCalculator({
    lineItems: watchedFields.lineItems,
    taxCalculation: watchedFields.taxCalculation,
  });

  const isSubmittable = useMemo(() => !!isDirty && !!isValid, [isDirty, isValid]);

  const lineEntries = useMemo(
    () =>
      lineItems.map((lineEntry, index) => ({
        ...lineEntry,
        automatedDraftIndex: index,
      })),
    [lineItems]
  );

  useEffect(() => {
    // Ensure that the passed lineItems are properly mapped to match dropdown options
    const mappedLineItems = lineItems.map((item) => ({
      ...item,
      account: dropdowns.accounts.find((option) => option.value === item.account?.value) || null,
      taxCode: dropdowns.taxCodes.find((option) => option.value === item.taxCode?.value) || null,
      class: dropdowns.classes.find((option) => option.value === item.class?.value) || null,
    }));

    // Update the default values with the mapped line items
    reset({
      lineItems: mappedLineItems.length > 0 ? mappedLineItems : [defaultLineEntryData],
      taxCalculation: taxCalculation,
    });
  }, [lineItems, dropdowns, taxCalculation, reset]);

  const handleCloseModal = useCallback(async () => {
    if (!isDirty) onClose();

    if (!(await confirmDiscard())) return;

    onClose();
  }, [isDirty, onClose, confirmDiscard]);

  const handleSubmit: SubmitHandler<LineItemsForm> = useCallback(
    (data) => {
      if (isAutomated) {
        Object(data.lineItems).forEach((lineItem, lineItemIndex) => {
          Object.entries(lineItem).forEach(([fieldKey, fieldValue]) => {
            if (lineEntries[lineItemIndex]) {
              const lineEntryValue = lineEntries[lineItemIndex][fieldKey];
              const parsedLineEntryValue = _.isNaN(Number(lineEntryValue))
                ? lineEntryValue
                : Number(lineEntryValue).toFixed(2);
              const formFieldValue = _.isObject(fieldValue)
                ? (fieldValue as DropdownOption).publishData
                : fieldValue;

              if (
                !_.isNull(parsedLineEntryValue) &&
                !_.isNull(formFieldValue) &&
                !_.isEqual(formFieldValue, parsedLineEntryValue) &&
                !_.isUndefined(lineItem[`${fieldKey}IsAutomated`])
              ) {
                data.lineItems[lineItemIndex][`${fieldKey}IsAutomated`] = false;
              }
            }
          });
        });
      }

      setLineItems(data.lineItems);
      setTaxCalculation(data.taxCalculation);

      onClose();
      showToast('success', 'Line details successfully saved');
    },
    [isAutomated, setLineItems, setTaxCalculation, onClose, lineEntries]
  );

  const handleQuantityBlur: TableEvents['onQuantityBlur'] = useCallback(
    (rowIndex, quantity) => {
      if (!quantity) return;
      const formattedQuantity = formatUnitPriceAndQuantity(quantity);
      setValue(`lineItems.${rowIndex}.qty`, formattedQuantity, { shouldValidate: true });

      const unitPrice = parseNumber(getValues(`lineItems.${rowIndex}.unitPrice`) || '');
      if (isNaN(unitPrice)) return;

      const totalAmount = TreatmentCalculator.calculateAmount(Number(formattedQuantity), unitPrice);

      setValue(`lineItems.${rowIndex}.totalAmount`, totalAmount, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [getValues, setValue]
  );

  const handleUnitPriceBlur: TableEvents['onUnitPriceBlur'] = useCallback(
    (rowIndex, unitPrice) => {
      if (!unitPrice) return;
      const formattedUnitPrice = formatUnitPriceAndQuantity(unitPrice);
      setValue(`lineItems.${rowIndex}.unitPrice`, formattedUnitPrice, { shouldValidate: true });

      const quantity = parseNumber(getValues(`lineItems.${rowIndex}.qty`) || '');
      if (isNaN(quantity)) return;

      const totalAmount = TreatmentCalculator.calculateAmount(Number(formattedUnitPrice), quantity);

      setValue(`lineItems.${rowIndex}.totalAmount`, totalAmount, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [getValues, setValue]
  );

  const handleRemove: TableEvents['onRemove'] = useCallback(
    (rowIndex) => {
      if (fields.length > 1) {
        remove(rowIndex);
      } else {
        // Reset the fields
        update(rowIndex, { ...defaultLineEntryData });
      }
    },
    [fields.length, remove, update]
  );

  const handleTaxCalculationChanged = useCallback(
    (taxCalculation: LineItemsForm['taxCalculation']) => {
      if (taxCalculation.value !== TaxCalculationEnum.NotApplicable) {
        trigger();
        return;
      }
      const lineItemsCount = getValues('lineItems').length;
      [...Array(lineItemsCount).keys()].forEach((index) => {
        setValue(`lineItems.${index}.taxCode`, null, { shouldValidate: true });
      });
    },
    [getValues, setValue]
  );

  const handleReset = useCallback(async () => {
    if (!(await onConfirmClearLines())) {
      return;
    }

    handleSubmit({
      lineItems: [],
      taxCalculation: dropdowns.taxCalculation[0],
    });
  }, [dropdowns.taxCalculation, onConfirmClearLines, handleSubmit]);

  const tableEvents = useMemo(
    (): TableEvents => ({
      onUnitPriceBlur: handleUnitPriceBlur,
      onQuantityBlur: handleQuantityBlur,
      onRemove: handleRemove,
      trigger,
    }),
    [handleUnitPriceBlur, handleRemove, handleQuantityBlur]
  );

  return (
    <div className={clsx('modal-body', 'tw-flex', 'tw-p-0', 'tw-max-h-[400px] tw-h-full')}>
      <div className={clsx('tw-p-[20px]', 'tw-w-full')}>
        <LineItemsTable
          fields={fields}
          register={register}
          control={control}
          dropdowns={dropdowns}
          events={tableEvents}
          taxCalculation={watchedFields.taxCalculation}
          draftLineEntries={lineEntries}
          isPrefilled={isPrefilled}
        />

        <div className={clsx('tw-flex tw-items-center', 'tw-pt-[15px] tw-pl-[10px]')}>
          <ActionButton
            className={clsx(
              'hover:tw-text-white hover:tw-border-cfo-blue-700 hover:tw-bg-cfo-blue-700'
            )}
            onClick={() => append({ ...defaultLineEntryData })}
            buttonType="primary_light_blue"
          >
            <Icon icon={ICONS.add} className={clsx('tw-text-[22px]')} />
            Add Line
          </ActionButton>

          <ActionButton
            className={clsx(
              'hover:tw-text-white hover:tw-border-cfo-blue-700 hover:tw-bg-cfo-blue-700'
            )}
            onClick={handleReset}
            buttonType="inverse"
          >
            Clear All Lines
          </ActionButton>
        </div>
      </div>

      <ComputationDisplayLayout>
        <div className={clsx('tw-w-full', 'tw-flex tw-justify-end')}>
          <ActionButton
            className={clsx('tw-p-0 tw-m-0')}
            buttonType={'style_unset'}
            onClick={handleCloseModal}
          >
            <Icon icon={ICONS.close} className={clsx('tw-text-[16px]')} />
          </ActionButton>
        </div>

        <Label
          label={'Amounts are'}
          required={true}
          mainContainerClasses={clsx('tw-mb-[17px]')}
          labelClasses={clsx('tw-text-cfo-black')}
        />
        <Controller
          name={`taxCalculation`}
          control={control}
          rules={{ required: getIsTaxRequired(watchedFields.taxCalculation) }}
          render={({ field }) => (
            <SelectField
              selected={field.value}
              options={dropdowns.taxCalculation}
              onChange={(value: LineItemsForm['taxCalculation']) => {
                field.onChange(value);
                handleTaxCalculationChanged(value);
              }}
              isAiGenerated={
                taxCalculationIsAutomated &&
                !!field.value &&
                _.isEqual(field.value.value, draftTaxCalculation)
              }
            />
          )}
        />

        <Computations
          totals={totals}
          currency={currency}
          className={clsx('tw-max-w-full tw-w-full', 'tw-mt-[100px] tw-mb-[30px]')}
        />

        <div className={clsx('tw-flex tw-items-center tw-justify-end')}>
          <ActionButton
            className={clsx(
              'hover:tw-text-white hover:tw-border-cfo-blue-700 hover:tw-bg-cfo-blue-700'
            )}
            onClick={handleCloseModal}
          >
            Cancel
          </ActionButton>

          <ActionButton
            disabled={!isSubmittable}
            className={clsx('tw-max-w-[110px] tw-w-full')}
            isActive={isSubmittable}
            onClick={formHandleSubmit(handleSubmit)}
          >
            Save & Close
          </ActionButton>
        </div>
      </ComputationDisplayLayout>
      <DiscardChangesModal />
    </div>
  );
};

export default LineItemsForm;
